import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import style from './WorkoutSession.module.css';
import { RoutineContext } from '../../contexts/RoutineContext';
import { UserContext } from '../../contexts/UserContext';
import { useAuthUser } from '../../hooks/useAuthUser';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { checkSubscription } from '../../utils/checkSubscription';
import { encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import findUserRank from '../../utils/findUserRank';
import Modal from '../Modal/Modal';
import RoutineCompletedModal from '../RoutineCompleteModal/RoutineCompleteModal';
import cling from '../../assets/cling.mp3';
import doneSound from '../../assets/done.mp3';
import {
	MdSkipNext, MdCheck, MdTimer, MdClose,
	MdFitnessCenter, MdSelfImprovement, MdArrowForward,
	MdLocalFireDepartment, MdVolumeUp, MdVolumeOff
} from 'react-icons/md';

const DEFAULT_SET_REST_DURATION = 20;
const DEFAULT_EXERCISE_REST_DURATION = 45;
const RADIUS = 88;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SHARE_IMAGE_WIDTH = 1200;
const SHARE_IMAGE_HEIGHT = 628;
const SESSION_STORAGE_TTL_MS = 1000 * 60 * 60 * 12;
const WORKOUT_DING_ENABLED_STORAGE_KEY = 'calcium:workout:dings-enabled';

function parseWorkoutParams(workout) {
	const rawSets = workout.workoutSets;
	const rawReps = workout.workoutReps;

	let sets = parseInt(rawSets);
	if (!Number.isFinite(sets) || sets < 1) sets = 1;
	sets = Math.min(sets, 100);

	const repsStr = String(rawReps || '').trim();
	let repsDisplay = repsStr || 'Complete when ready';
	let minTimePerSet = 30;

	if (repsStr) {
		const numMatch = repsStr.match(/^(\d+)/);
		if (numMatch) {
			const num = parseInt(numMatch[1]);
			const lower = repsStr.toLowerCase();

			if (lower.includes('sec') || lower.match(/^\d+\s*s$/) || lower.match(/^\d+\s*s\b/)) {
				minTimePerSet = num;
			} else if (lower.includes('min')) {
				minTimePerSet = num * 60;
			} else {
				minTimePerSet = Math.max(num * 3, 15);
			}
		}
	}

	const parseRestDuration = (value) => {
		if (value == null) return null;
		if (typeof value === 'number' && Number.isFinite(value) && value > 0) return Math.round(value);
		const raw = String(value).trim().toLowerCase();
		if (!raw) return null;
		const numMatch = raw.match(/(\d+(\.\d+)?)/);
		if (!numMatch) return null;
		const amount = parseFloat(numMatch[1]);
		if (!Number.isFinite(amount) || amount <= 0) return null;
		if (raw.includes('min')) return Math.round(amount * 60);
		return Math.round(amount);
	};

	const resolveRestFromKeys = (keys, fallback = null) => {
		for (const key of keys) {
			const parsed = parseRestDuration(workout?.[key]);
			if (parsed != null) return parsed;
		}
		return fallback;
	};

	const parseRestFromRepsText = (value) => {
		const raw = String(value || '').trim().toLowerCase();
		if (!raw) return null;

		// Supports interval formats like "20s on / 40s off".
		const intervalOffMatch = raw.match(/\/\s*(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes|s|sec|secs|second|seconds)\s*off\b/);
		if (intervalOffMatch) {
			return parseRestDuration(`${intervalOffMatch[1]} ${intervalOffMatch[2]}`);
		}

		// Supports "rest 60s", "rest: 90 sec", etc.
		const restMatch = raw.match(/\brest\b[^0-9]*(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes|s|sec|secs|second|seconds)?/);
		if (restMatch) {
			const unit = restMatch[2] || 's';
			return parseRestDuration(`${restMatch[1]} ${unit}`);
		}

		return null;
	};

	const restFromRepsText = parseRestFromRepsText(repsStr);

	const setRestSeconds = resolveRestFromKeys([
		'setRestSeconds',
		'restBetweenSetsSeconds',
		'setRest',
		'restSeconds',
		'rest'
	], restFromRepsText ?? DEFAULT_SET_REST_DURATION);

	const exerciseRestSeconds = resolveRestFromKeys([
		'exerciseRestSeconds',
		'restBetweenExercisesSeconds',
		'exerciseRest',
	], DEFAULT_EXERCISE_REST_DURATION);

	return {
		workoutType: workout.workoutType || 'Exercise',
		sets,
		repsDisplay,
		minTimePerSet,
		setRestSeconds,
		exerciseRestSeconds,
	};
}

function formatTime(totalSeconds) {
	const hrs = Math.floor(totalSeconds / 3600);
	const mins = Math.floor((totalSeconds % 3600) / 60);
	const secs = totalSeconds % 60;
	if (hrs > 0) return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
	return `${mins}:${String(secs).padStart(2, '0')}`;
}

function playAudio(src) {
	try {
		const a = new Audio(src);
		a.play().catch(() => {});
	} catch {}
}

function parseRepValue(repsDisplay) {
	const match = String(repsDisplay || '').match(/^(\d+)/);
	if (!match) return 0;
	const value = parseInt(match[1], 10);
	return Number.isFinite(value) ? value : 0;
}

const WorkoutSession = ({
	workouts,
	routineName,
	onEnd,
	onSessionComplete,
	onShareCompletion,
	shareLink,
}) => {
	const { userID } = useParams();
	const { user } = useAuthUser();
	const { dbUser, setDbUser } = useContext(UserContext);
	const { setCurrentXP, setCurrentProgress } = useContext(RoutineContext);
	const API_URL = process.env.REACT_APP_API_URL;
	const requestApi = useCallback((f) => requestWithApiFallback(API_URL, f), [API_URL]);

	const parsed = workouts.map(parseWorkoutParams);
	const totalSetsAll = parsed.reduce((s, p) => s + p.sets, 0);
	const percentPerSet = totalSetsAll > 0 ? 100 / totalSetsAll : 100;

	const [workoutIdx, setWorkoutIdx] = useState(0);
	const [setNum, setSetNum] = useState(1);
	const [phase, setPhase] = useState('workout');
	const [timeRemaining, setTimeRemaining] = useState(0);
	const [totalMinTime, setTotalMinTime] = useState(0);
	const [sessionElapsed, setSessionElapsed] = useState(0);
	const [results, setResults] = useState([]);
	const [animatingOut, setAnimatingOut] = useState(false);
	const [streakData, setStreakData] = useState(null);
	const [rankInfo, setRankInfo] = useState(null);
	const [isExitModalOpen, setIsExitModalOpen] = useState(false);
	const [showCompletionModal, setShowCompletionModal] = useState(false);
	const [showXPGain, setShowXPGain] = useState(false);
	const [xpGainTick, setXpGainTick] = useState(0);
	const [shareImageDataUrl, setShareImageDataUrl] = useState('');
	const [isDingEnabled, setIsDingEnabled] = useState(true);

	const sessionRef = useRef(null);
	const xpGainTimeoutRef = useRef(null);
	const phaseJustInitializedRef = useRef(false);
	const restoreAttemptedKeyRef = useRef(null);
	const pendingRestoreTimerRef = useRef(null);
	const hasBackGuardRef = useRef(false);
	const suppressGuardPopRef = useRef(false);
	const backCloseRequestedRef = useRef(false);
	const cur = parsed[workoutIdx];
	const sessionStorageKey = useMemo(() => {
		const ownerSegment = encodeURIComponent(String(userID || 'owner'));
		const routineSegment = encodeURIComponent(String(routineName || 'workout'));
		const workoutSignature = parsed
			.map((item) => `${item.workoutType}:${item.sets}:${item.repsDisplay}`)
			.join('|')
			.slice(0, 220);
		return `calcium:active-workout:v2:${ownerSegment}:${routineSegment}:${workoutSignature}`;
	}, [parsed, routineName, userID]);

	useEffect(() => {
		sessionRef.current = setInterval(() => setSessionElapsed(p => p + 1), 1000);
		return () => clearInterval(sessionRef.current);
	}, []);

	useEffect(() => {
		return () => {
			if (xpGainTimeoutRef.current) {
				clearTimeout(xpGainTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		try {
			const raw = window.localStorage.getItem(WORKOUT_DING_ENABLED_STORAGE_KEY);
			if (raw == null) return;
			setIsDingEnabled(raw !== 'false');
		} catch {}
	}, []);

	useEffect(() => {
		try {
			window.localStorage.setItem(WORKOUT_DING_ENABLED_STORAGE_KEY, String(isDingEnabled));
		} catch {}
	}, [isDingEnabled]);

	useEffect(() => {
		let t = 0;
		if (phase === 'workout' && cur) t = cur.minTimePerSet;
		else if (phase === 'set-rest') t = cur?.setRestSeconds || DEFAULT_SET_REST_DURATION;
		else if (phase === 'exercise-rest') t = cur?.exerciseRestSeconds || DEFAULT_EXERCISE_REST_DURATION;

		const pendingRestore = pendingRestoreTimerRef.current;
		const canUsePendingRestore = pendingRestore
			&& pendingRestore.phase === phase
			&& pendingRestore.workoutIdx === workoutIdx
			&& pendingRestore.setNum === setNum;

		if (canUsePendingRestore) {
			const restoredRemaining = Math.max(0, Number(pendingRestore.timeRemaining) || 0);
			phaseJustInitializedRef.current = restoredRemaining > 0;
			setTotalMinTime(Number(pendingRestore.totalMinTime) || t);
			setTimeRemaining(restoredRemaining);
			pendingRestoreTimerRef.current = null;
			return;
		}

		phaseJustInitializedRef.current = true;
		setTotalMinTime(t);
		setTimeRemaining(t);
	}, [workoutIdx, setNum, phase]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (timeRemaining <= 0 || (phase !== 'workout' && phase !== 'set-rest' && phase !== 'exercise-rest')) return;
		const id = setTimeout(() => setTimeRemaining(p => p - 1), 1000);
		return () => clearTimeout(id);
	}, [timeRemaining, phase]);

	useEffect(() => {
		// Ignore the first zero-value frame immediately after phase changes.
		if (phaseJustInitializedRef.current && timeRemaining > 0) {
			phaseJustInitializedRef.current = false;
		}
	}, [timeRemaining]);

	useEffect(() => {
		if (phaseJustInitializedRef.current) return;
		if (timeRemaining === 0 && phase === 'workout' && isDingEnabled) playAudio(cling);
		if (timeRemaining === 0 && phase === 'set-rest') handleSetRestDone();
		if (timeRemaining === 0 && phase === 'exercise-rest') handleExerciseRestDone();
	}, [timeRemaining, phase, isDingEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (phase === 'complete') return undefined;
		const handleBeforeUnload = (event) => {
			event.preventDefault();
			event.returnValue = '';
			return '';
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [phase]);

	const removeBackGuard = useCallback(() => {
		if (!hasBackGuardRef.current) return;
		suppressGuardPopRef.current = true;
		hasBackGuardRef.current = false;
		window.history.back();
	}, []);

	useEffect(() => {
		if (phase === 'complete') return undefined;
		if (!hasBackGuardRef.current) {
			window.history.pushState({ calciumWorkoutSessionGuard: true }, '', window.location.href);
			hasBackGuardRef.current = true;
		}

		const handlePopState = () => {
			if (suppressGuardPopRef.current) {
				suppressGuardPopRef.current = false;
				return;
			}
			if (phase === 'complete') return;
			backCloseRequestedRef.current = true;
			setIsExitModalOpen(true);
			window.history.pushState({ calciumWorkoutSessionGuard: true }, '', window.location.href);
			hasBackGuardRef.current = true;
		};

		window.addEventListener('popstate', handlePopState);
		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [phase]);

	const clearPersistedSession = useCallback(() => {
		if (!sessionStorageKey) return;
		try {
			window.localStorage.removeItem(sessionStorageKey);
		} catch {}
	}, [sessionStorageKey]);

	useEffect(() => {
		if (!sessionStorageKey || parsed.length === 0) return;
		if (restoreAttemptedKeyRef.current === sessionStorageKey) return;
		restoreAttemptedKeyRef.current = sessionStorageKey;
		try {
			const raw = window.localStorage.getItem(sessionStorageKey);
			if (!raw) return;
			const saved = JSON.parse(raw);
			if (!saved || typeof saved !== 'object') {
				clearPersistedSession();
				return;
			}
			if (saved.phase === 'complete') {
				clearPersistedSession();
				return;
			}
			if (Date.now() - Number(saved.updatedAt || 0) > SESSION_STORAGE_TTL_MS) {
				clearPersistedSession();
				return;
			}

			const nextWorkoutIdx = Math.max(0, Math.min(parsed.length - 1, Number(saved.workoutIdx) || 0));
			const nextSetsForWorkout = parsed[nextWorkoutIdx]?.sets || 1;
			const nextSetNum = Math.max(1, Math.min(nextSetsForWorkout, Number(saved.setNum) || 1));
			const nextPhase = ['workout', 'set-rest', 'exercise-rest'].includes(saved.phase) ? saved.phase : 'workout';
			const secondsSinceSave = Math.floor((Date.now() - Number(saved.updatedAt || Date.now())) / 1000);
			const restoredTimeRemaining = Math.max(0, (Number(saved.timeRemaining) || 0) - secondsSinceSave);
			const restoredTotalMinTime = Math.max(0, Number(saved.totalMinTime) || 0);
			const restoredResults = Array.isArray(saved.results) ? saved.results : [];
			const restoredElapsed = Math.max(0, Number(saved.sessionElapsed) || 0);

			pendingRestoreTimerRef.current = {
				phase: nextPhase,
				workoutIdx: nextWorkoutIdx,
				setNum: nextSetNum,
				timeRemaining: restoredTimeRemaining,
				totalMinTime: restoredTotalMinTime
			};

			setWorkoutIdx(nextWorkoutIdx);
			setSetNum(nextSetNum);
			setPhase(nextPhase);
			setResults(restoredResults);
			setSessionElapsed(restoredElapsed);

			const completedSets = restoredResults.filter((entry) => entry.status === 'completed').length;
			setCurrentXP(completedSets * 10);
			setCurrentProgress(Math.min(100, parseFloat((completedSets * percentPerSet).toFixed(1))));
		} catch {
			clearPersistedSession();
		}
	}, [clearPersistedSession, parsed, percentPerSet, sessionStorageKey, setCurrentProgress, setCurrentXP]);

	useEffect(() => {
		if (!sessionStorageKey || phase === 'complete') return;
		const payload = {
			workoutIdx,
			setNum,
			phase,
			timeRemaining,
			totalMinTime,
			sessionElapsed,
			results,
			updatedAt: Date.now(),
		};
		try {
			window.localStorage.setItem(sessionStorageKey, JSON.stringify(payload));
		} catch {}
	}, [phase, results, sessionElapsed, sessionStorageKey, setNum, timeRemaining, totalMinTime, workoutIdx]);

	const canComplete = timeRemaining <= 0 && phase === 'workout';

	const transition = (cb) => {
		setAnimatingOut(true);
		setTimeout(() => { cb(); setAnimatingOut(false); }, 280);
	};

	const handleCompleteSet = async () => {
		if (!canComplete) return;
		if (isDingEnabled) playAudio(cling);

		setResults(p => [...p, { workoutIdx, setNum, status: 'completed' }]);

		const hasSubscription = checkSubscription(dbUser);
		if (hasSubscription && user?.id) {
			setCurrentXP(prev => prev + 10);
			setCurrentProgress(prev => Math.min(100, parseFloat((prev + percentPerSet).toFixed(1))));
			setXpGainTick((tick) => tick + 1);
			setShowXPGain(true);
			if (xpGainTimeoutRef.current) clearTimeout(xpGainTimeoutRef.current);
			xpGainTimeoutRef.current = setTimeout(() => setShowXPGain(false), 1050);

			requestApi((base) => axios.post(
				`${base}/stryds/increment-routine-xp/${encodePathSegment(userID)}/${encodePathSegment(routineName)}`,
				{ userID: user.id, userRoutineXP: 10 }
			)).catch(() => {});
			requestApi((base) => axios.post(
				`${base}/users/increment-total-xp/${encodePathSegment(user.id)}`,
				{ userXP: 10 }
			)).catch(() => {});
		}

		if (setNum < cur.sets) {
			transition(() => setPhase('set-rest'));
		} else if (workoutIdx < workouts.length - 1) {
			transition(() => setPhase('exercise-rest'));
		} else {
			transition(() => finishSession());
		}
	};

	const toggleDingEnabled = () => {
		setIsDingEnabled((prev) => !prev);
	};

	const handleSkipSet = () => {
		setResults(p => [...p, { workoutIdx, setNum, status: 'skipped' }]);

		if (setNum < cur.sets) {
			transition(() => setPhase('set-rest'));
		} else if (workoutIdx < workouts.length - 1) {
			transition(() => setPhase('exercise-rest'));
		} else {
			transition(() => finishSession());
		}
	};

	const handleSetRestDone = () => {
		transition(() => { setSetNum(p => p + 1); setPhase('workout'); });
	};

	const handleExerciseRestDone = () => {
		transition(() => { setWorkoutIdx(p => p + 1); setSetNum(1); setPhase('workout'); });
	};

	const finishSession = async () => {
		setPhase('complete');
		playAudio(doneSound);
		clearInterval(sessionRef.current);
		setShowCompletionModal(true);
		clearPersistedSession();

		const completedSets = results.filter((entry) => entry.status === 'completed').length;
		const xpEarned = completedSets * 10;
		if (onSessionComplete) {
			onSessionComplete({
				routineName,
				xpEarned,
				completedSets,
				totalSets: totalSetsAll,
				durationSeconds: sessionElapsed,
			});
		}

		if (user?.id) {
			const estimatedMinutes = parsed.reduce((sum, workout) => (
				sum + ((workout.minTimePerSet * workout.sets) / 60)
			), 0);
			try {
				const streakRes = await requestApi((base) =>
					axios.post(`${base}/streaks/log-workout/${encodePathSegment(user.id)}`, {
						routineName: routineName || 'Unknown',
						routineCreatorID: userID || undefined,
						durationMinutes: Math.round(sessionElapsed / 60),
						estimatedMinutes: Math.round(estimatedMinutes),
						xpEarned: completedSets * 10,
						workoutsCompleted: completedSets,
					})
				);
				setStreakData(streakRes.data.streak);
			} catch {}

			try {
				const userRes = await requestApi((base) =>
					axios.get(`${base}/users/${encodePathSegment(user.id)}`)
				);
				const u = userRes.data?.[0] || userRes.data;
				if (u?.totalXP !== undefined) {
					setRankInfo(findUserRank(u.totalXP));
				}
				if (u) {
					setDbUser((prev) => {
						if (!prev) return u;
						const hadPaidPlan = checkSubscription(prev);
						const merged = { ...prev };
						Object.keys(u || {}).forEach((key) => {
							if (u[key] !== undefined) {
								merged[key] = u[key];
							}
						});
						// Never downgrade a known paid user to free because of a partial/stale response.
						if (hadPaidPlan && !checkSubscription(merged)) {
							merged.userPlan = prev.userPlan;
						}
						return merged;
					});
				}
			} catch {}
		}
	};

	const handleEndSession = () => {
		removeBackGuard();
		clearInterval(sessionRef.current);
		setShowCompletionModal(false);
		clearPersistedSession();
		backCloseRequestedRef.current = false;
		onEnd();
	};

	const handleConfirmEndSession = () => {
		if (phase === 'complete') {
			handleEndSession();
			return;
		}
		setIsExitModalOpen(true);
	};

	const totalXPEarned = results.filter(r => r.status === 'completed').length * 10;
	const exerciseResults = parsed.map((p, i) => {
		const sets = results.filter(r => r.workoutIdx === i);
		const done = sets.filter(r => r.status === 'completed').length;
		const skipped = sets.filter(r => r.status === 'skipped').length;
		return { ...p, completed: done, skipped, total: p.sets };
	});

	const overallCompletedSets = results.filter(r => r.status === 'completed').length;
	const overallProgress = totalSetsAll > 0 ? (overallCompletedSets / totalSetsAll) * 100 : 0;
	const overallCompletedReps = results.reduce((sum, entry) => {
		if (entry.status !== 'completed') return sum;
		const workout = parsed[entry.workoutIdx];
		return sum + parseRepValue(workout?.repsDisplay);
	}, 0);

	const timerProgress = totalMinTime > 0 ? (totalMinTime - timeRemaining) / totalMinTime : 1;
	const strokeOffset = CIRCUMFERENCE * (1 - timerProgress);
	const sessionScore = totalXPEarned;
	const displayUsername = dbUser?.username || user?.username || user?.firstName || 'Athlete';
	const shareStats = {
		xpEarned: totalXPEarned,
		setsCompleted: overallCompletedSets,
		repsCompleted: overallCompletedReps,
		duration: formatTime(sessionElapsed),
	};

	const buildShareImage = useCallback(() => {
		const canvas = document.createElement('canvas');
		canvas.width = SHARE_IMAGE_WIDTH;
		canvas.height = SHARE_IMAGE_HEIGHT;
		const ctx = canvas.getContext('2d');
		if (!ctx) return '';

		const gradient = ctx.createLinearGradient(0, 0, SHARE_IMAGE_WIDTH, SHARE_IMAGE_HEIGHT);
		gradient.addColorStop(0, '#0f172a');
		gradient.addColorStop(1, '#111827');
		ctx.fillStyle = gradient;
		ctx.fillRect(0, 0, SHARE_IMAGE_WIDTH, SHARE_IMAGE_HEIGHT);

		ctx.fillStyle = 'rgba(16, 185, 129, 0.16)';
		ctx.beginPath();
		ctx.arc(1020, 120, 220, 0, Math.PI * 2);
		ctx.fill();
		ctx.beginPath();
		ctx.arc(180, 560, 260, 0, Math.PI * 2);
		ctx.fill();

		ctx.fillStyle = '#93c5fd';
		ctx.font = '700 34px Arial';
		ctx.fillText('CALCIUM SESSION', 72, 86);

		ctx.fillStyle = '#f8fafc';
		ctx.font = '800 64px Arial';
		ctx.fillText(String(routineName || 'Workout Session'), 72, 168);

		ctx.fillStyle = '#cbd5e1';
		ctx.font = '500 36px Arial';
		ctx.fillText(`by @${displayUsername}`, 72, 220);

		const cards = [
			{ label: 'XP Earned', value: `${shareStats.xpEarned}` },
			{ label: 'Sets Completed', value: `${shareStats.setsCompleted}` },
			{ label: 'Reps Completed', value: `${shareStats.repsCompleted}` },
			{ label: 'Duration', value: `${shareStats.duration}` },
		];
		const cardY = 292;
		const cardGap = 24;
		const cardWidth = 246;
		const cardHeight = 188;

		cards.forEach((card, idx) => {
			const x = 72 + (idx * (cardWidth + cardGap));
			ctx.fillStyle = 'rgba(15, 23, 42, 0.7)';
			ctx.strokeStyle = 'rgba(148, 163, 184, 0.24)';
			ctx.lineWidth = 2;
			ctx.beginPath();
			ctx.roundRect(x, cardY, cardWidth, cardHeight, 18);
			ctx.fill();
			ctx.stroke();

			ctx.fillStyle = '#94a3b8';
			ctx.font = '600 24px Arial';
			ctx.fillText(card.label.toUpperCase(), x + 22, cardY + 52);

			ctx.fillStyle = '#f8fafc';
			ctx.font = '800 56px Arial';
			ctx.fillText(card.value, x + 22, cardY + 126);
		});

		ctx.fillStyle = '#86efac';
		ctx.font = '600 24px Arial';
		ctx.fillText('No exercise names shown', 72, 586);

		ctx.fillStyle = '#cbd5e1';
		ctx.font = '500 22px Arial';
		ctx.fillText('calcium.fitness', 1018, 586);

		return canvas.toDataURL('image/png');
	}, [displayUsername, routineName, shareStats.duration, shareStats.repsCompleted, shareStats.setsCompleted, shareStats.xpEarned]);

	useEffect(() => {
		if (phase !== 'complete') return;
		setShareImageDataUrl(buildShareImage());
	}, [buildShareImage, phase]);

	const setDotsForCurrent = cur ? Array.from({ length: cur.sets }, (_, i) => {
		const r = results.find(res => res.workoutIdx === workoutIdx && res.setNum === i + 1);
		if (r?.status === 'completed') return 'done';
		if (i + 1 === setNum && phase === 'workout') return 'active';
		if (i + 1 < setNum) return 'done';
		return 'pending';
	}) : [];

	// ─── COMPLETE PHASE ───
	if (phase === 'complete') {
		return (
			<div className={style.SessionWrapper}>
				<div className={`${style.PhaseContainer} ${style.CompleteScreen}`}>
					<div className={style.CompleteBadge}>
						<MdFitnessCenter className={style.CompleteBadgeIcon} />
					</div>
					<h2 className={style.CompleteTitle}>Session Complete</h2>
					<p className={style.CompleteSubtitle}>
						{totalXPEarned > 0 ? 'You showed up and put in the work.' : 'Try completing some sets next time!'}
					</p>

					<div className={style.CompleteStats}>
						<div className={style.StatCard}>
							<span className={style.StatValue}>{formatTime(sessionElapsed)}</span>
							<span className={style.StatLabel}>Total Time</span>
						</div>
						<div className={style.StatCard}>
							<span className={style.StatValue}>{overallCompletedSets}<span className={style.StatDenom}>/{totalSetsAll}</span></span>
							<span className={style.StatLabel}>Sets Done</span>
						</div>
						<div className={style.StatCard}>
							<span className={style.StatValue}>{totalXPEarned}</span>
							<span className={style.StatLabel}>XP Earned</span>
						</div>
					</div>

					{streakData && streakData.currentStreak > 0 && (
						<div className={style.StreakCard}>
							<MdLocalFireDepartment className={style.StreakIcon} />
							<div className={style.StreakInfo}>
								<span className={style.StreakValue}>{streakData.currentStreak} day streak</span>
								<span className={style.StreakSub}>
									{streakData.currentStreak >= streakData.longestStreak
										? 'New personal best!'
										: `Best: ${streakData.longestStreak} days`}
								</span>
							</div>
						</div>
					)}

					{rankInfo && (
						<div className={style.RankCard}>
							<span className={style.RankEmoji}>{rankInfo.emoji}</span>
							<div className={style.RankInfo}>
								<span className={style.RankName}>{rankInfo.rank}</span>
								<span className={style.RankVibe}>{rankInfo.vibe}</span>
							</div>
						</div>
					)}

					<div className={style.CompleteBreakdown}>
						<span className={style.BreakdownTitle}>Breakdown</span>
						{exerciseResults.map((ex, i) => (
							<div key={i} className={style.BreakdownRow}>
								<span className={style.BreakdownIndex}>{i + 1}</span>
								<div className={style.BreakdownMain}>
									<span className={style.BreakdownName}>{ex.workoutType}</span>
									<span className={style.BreakdownMeta}>
										{ex.completed}/{ex.total} sets
									</span>
								</div>
								{ex.completed > 0 ? (
									<span className={style.BreakdownCompleted}><MdCheck /> +{ex.completed * 10} XP</span>
								) : (
									<span className={style.BreakdownSkipped}>Skipped</span>
								)}
							</div>
						))}
					</div>

					<button className={style.FinishBtn} onClick={handleEndSession}>
						Finish
					</button>
					<button className={style.ShareBtn} onClick={() => setShowCompletionModal(true)}>
						Share
					</button>
				</div>
				{showCompletionModal && (
					<RoutineCompletedModal
						onClose={() => setShowCompletionModal(false)}
						xp={totalXPEarned}
						workoutName={routineName}
						onShare={onShareCompletion}
						shareLink={shareLink}
						onFinish={handleEndSession}
						shareImageDataUrl={shareImageDataUrl}
						shareMeta={{
							username: displayUsername,
							...shareStats,
						}}
					/>
				)}
			</div>
		);
	}

	// ─── SET REST PHASE (between sets of same exercise) ───
	if (phase === 'set-rest') {
		const restProgress = totalMinTime > 0 ? (totalMinTime - timeRemaining) / totalMinTime : 0;
		const restOffset = CIRCUMFERENCE * (1 - restProgress);

		return (
			<div className={style.SessionWrapper}>
				<div className={`${style.PhaseContainer} ${style.RestScreen} ${animatingOut ? style.FadeOut : style.FadeIn}`}>
					<div className={style.SessionTopBar}>
						<div className={style.SessionProgressBar}>
							<span className={style.ProgressLabel}>{cur.workoutType} · Set {setNum} of {cur.sets} done</span>
							<div className={style.ProgressTrack}>
								<div className={style.ProgressFill} style={{ width: `${overallProgress}%` }} />
							</div>
						</div>
						<div className={style.ElapsedTime}>
							<MdTimer />
							<span>{formatTime(sessionElapsed)}</span>
						</div>
						<div className={style.SessionScorePill}>{sessionScore} XP</div>
						<button
							type='button'
							className={style.SoundToggleBtn}
							onClick={toggleDingEnabled}
							aria-label={isDingEnabled ? 'Mute workout ding' : 'Unmute workout ding'}
							title={isDingEnabled ? 'Mute workout ding' : 'Unmute workout ding'}
						>
							{isDingEnabled ? <MdVolumeUp /> : <MdVolumeOff />}
						</button>
					</div>

					<div className={style.RestBody}>
						<span className={style.RestLabel}>Set rest</span>

						<div className={style.TimerRing}>
							<svg viewBox="0 0 200 200" className={style.TimerSVG}>
								<circle cx="100" cy="100" r={RADIUS} className={style.RingTrack} />
								<circle cx="100" cy="100" r={RADIUS} className={style.RingFillRest}
									style={{ strokeDasharray: CIRCUMFERENCE, strokeDashoffset: restOffset }}
								/>
							</svg>
							<div className={style.TimerCenter}>
								<span className={style.TimerCenterValue}>{formatTime(timeRemaining)}</span>
							</div>
						</div>

						<div className={style.UpNextCard}>
							<span className={style.UpNextTag}>Next</span>
							<span className={style.UpNextName}>{cur.workoutType}</span>
							<span className={style.UpNextMeta}>Set {setNum + 1} of {cur.sets} · {cur.repsDisplay}</span>
						</div>

						<div className={style.SetDots}>
							{setDotsForCurrent.map((s, i) => (
								<span key={i} className={`${style.SetDot} ${s === 'done' ? style.SetDotDone : ''} ${s === 'active' ? style.SetDotActive : ''}`} />
							))}
						</div>

						<button className={style.SkipRestBtn} onClick={handleSetRestDone}>
							Skip Rest <MdArrowForward />
						</button>
					</div>
				</div>
			</div>
		);
	}

	// ─── EXERCISE REST PHASE (between different exercises) ───
	if (phase === 'exercise-rest') {
		const nextWorkout = parsed[workoutIdx + 1];
		const restProgress = totalMinTime > 0 ? (totalMinTime - timeRemaining) / totalMinTime : 0;
		const restOffset = CIRCUMFERENCE * (1 - restProgress);

		return (
			<div className={style.SessionWrapper}>
				<div className={`${style.PhaseContainer} ${style.RestScreen} ${animatingOut ? style.FadeOut : style.FadeIn}`}>
					<div className={style.SessionTopBar}>
						<div className={style.SessionProgressBar}>
							<span className={style.ProgressLabel}>Exercise {workoutIdx + 1} of {workouts.length} done</span>
							<div className={style.ProgressTrack}>
								<div className={style.ProgressFill} style={{ width: `${overallProgress}%` }} />
							</div>
						</div>
						<div className={style.ElapsedTime}>
							<MdTimer />
							<span>{formatTime(sessionElapsed)}</span>
						</div>
						<div className={style.SessionScorePill}>{sessionScore} XP</div>
						<button
							type='button'
							className={style.SoundToggleBtn}
							onClick={toggleDingEnabled}
							aria-label={isDingEnabled ? 'Mute workout ding' : 'Unmute workout ding'}
							title={isDingEnabled ? 'Mute workout ding' : 'Unmute workout ding'}
						>
							{isDingEnabled ? <MdVolumeUp /> : <MdVolumeOff />}
						</button>
					</div>

					<div className={style.RestBody}>
						<MdSelfImprovement className={style.RestIcon} />
						<span className={style.RestLabel}>Take a breather</span>

						<div className={style.TimerRing}>
							<svg viewBox="0 0 200 200" className={style.TimerSVG}>
								<circle cx="100" cy="100" r={RADIUS} className={style.RingTrack} />
								<circle cx="100" cy="100" r={RADIUS} className={style.RingFillRest}
									style={{ strokeDasharray: CIRCUMFERENCE, strokeDashoffset: restOffset }}
								/>
							</svg>
							<div className={style.TimerCenter}>
								<span className={style.TimerCenterValue}>{formatTime(timeRemaining)}</span>
							</div>
						</div>

						{nextWorkout && (
							<div className={style.UpNextCard}>
								<span className={style.UpNextTag}>Up Next</span>
								<span className={style.UpNextName}>{nextWorkout.workoutType}</span>
								<span className={style.UpNextMeta}>
									{nextWorkout.sets} set{nextWorkout.sets !== 1 ? 's' : ''} · {nextWorkout.repsDisplay}
								</span>
							</div>
						)}

						<button className={style.SkipRestBtn} onClick={handleExerciseRestDone}>
							Skip Rest <MdArrowForward />
						</button>
					</div>
				</div>
			</div>
		);
	}

	// ─── WORKOUT PHASE (individual set) ───
	const remainingExercises = parsed.slice(workoutIdx + 1);

	return (
		<div className={style.SessionWrapper}>
			<div className={`${style.PhaseContainer} ${style.WorkoutScreen} ${animatingOut ? style.FadeOut : style.FadeIn}`}>
				<div className={style.SessionTopBar}>
					<div className={style.SessionProgressBar}>
						<span className={style.ProgressLabel}>Exercise {workoutIdx + 1} of {workouts.length}</span>
						<div className={style.ProgressTrack}>
							<div className={style.ProgressFill} style={{ width: `${overallProgress}%` }} />
						</div>
					</div>
					<div className={style.ElapsedTime}>
						<MdTimer />
						<span>{formatTime(sessionElapsed)}</span>
					</div>
					<div className={style.SessionScoreWrap}>
						<div className={style.SessionScorePill}>{sessionScore} XP</div>
						{showXPGain && (
							<span key={xpGainTick} className={style.SessionXPGain}>
								+10 XP
							</span>
						)}
					</div>
					<button
						type='button'
						className={style.SoundToggleBtn}
						onClick={toggleDingEnabled}
						aria-label={isDingEnabled ? 'Mute workout ding' : 'Unmute workout ding'}
						title={isDingEnabled ? 'Mute workout ding' : 'Unmute workout ding'}
					>
						{isDingEnabled ? <MdVolumeUp /> : <MdVolumeOff />}
					</button>
					<button className={style.ExitBtn} onClick={handleConfirmEndSession} aria-label="End session">
						<MdClose />
					</button>
				</div>

				<div className={style.WorkoutBody}>
					<h1 className={style.ExerciseName}>{cur.workoutType}</h1>

					{cur.sets > 1 && (
						<span className={style.SetIndicator}>Set {setNum} of {cur.sets}</span>
					)}

					<span className={style.RepsDisplay}>{cur.repsDisplay}</span>

					<div className={style.SetDots}>
						{setDotsForCurrent.map((s, i) => (
							<span key={i} className={`${style.SetDot} ${s === 'done' ? style.SetDotDone : ''} ${s === 'active' ? style.SetDotActive : ''}`} />
						))}
					</div>

					<div className={style.TimerRing}>
						<svg viewBox="0 0 200 200" className={style.TimerSVG}>
							<circle cx="100" cy="100" r={RADIUS} className={style.RingTrack} />
							<circle cx="100" cy="100" r={RADIUS}
								className={canComplete ? style.RingFillReady : style.RingFill}
								style={{ strokeDasharray: CIRCUMFERENCE, strokeDashoffset: strokeOffset }}
							/>
						</svg>
						<div className={style.TimerCenter}>
							{canComplete ? (
								<>
									<MdCheck className={style.TimerReadyIcon} />
									<span className={style.TimerReadyText}>Ready</span>
								</>
							) : (
								<>
									<span className={style.TimerCenterValue}>{formatTime(timeRemaining)}</span>
									<span className={style.TimerCenterLabel}>minimum</span>
								</>
							)}
						</div>
					</div>

					<a
						href={`https://www.youtube.com/results?search_query=how+to+${encodeURIComponent(cur.workoutType)}+exercise`}
						target="_blank"
						rel="noreferrer"
						className={style.DemoLink}
					>
						Watch demo
					</a>
				</div>

				<div className={style.ActionBar}>
					<button
						className={`${style.CompleteBtn} ${canComplete ? style.CompleteBtnReady : ''}`}
						onClick={handleCompleteSet}
						disabled={!canComplete}
					>
						<MdCheck />
						<span>Complete</span>
					</button>
					<button className={style.SkipBtn} onClick={handleSkipSet}>
						<MdSkipNext />
						<span>Skip Reps</span>
					</button>
				</div>

				{remainingExercises.length > 0 && (
					<div className={style.UpcomingList}>
						<span className={style.UpcomingHeading}>Coming Up</span>
						{remainingExercises.slice(0, 3).map((w, i) => (
							<div key={i} className={style.UpcomingRow}>
								<span className={style.UpcomingNum}>{workoutIdx + 2 + i}</span>
								<span className={style.UpcomingExName}>{w.workoutType}</span>
								<span className={style.UpcomingExMeta}>{w.sets}×{w.repsDisplay}</span>
							</div>
						))}
						{remainingExercises.length > 3 && (
							<span className={style.UpcomingMore}>+{remainingExercises.length - 3} more</span>
						)}
					</div>
				)}
			</div>
			{isExitModalOpen && (
				<Modal
					onClose={() => setIsExitModalOpen(false)}
					title='Close session?'
					subtitle='All progress from this current session will be lost.'
					maxWidth={480}
				>
					<div className={style.ExitConfirmBody}>
						<p className={style.ExitConfirmText}>
							Are you sure you want to close this workout session now?
						</p>
						<div className={style.ExitConfirmActions}>
							<button
								type='button'
								className={style.ExitConfirmKeepBtn}
								onClick={() => {
									backCloseRequestedRef.current = false;
									setIsExitModalOpen(false);
								}}
							>
								Keep session
							</button>
							<button
								type='button'
								className={style.ExitConfirmLeaveBtn}
								onClick={handleEndSession}
							>
								Close session
							</button>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default WorkoutSession;
